import { useState, useEffect, useCallback } from 'react';
import { LuX, LuCheck } from 'react-icons/lu';
import TraderLayout from '../../components/TraderLayout/TraderLayout';
import TraderOrderDrawer from '../../components/TraderOrderDrawer/TraderOrderDrawer';
import { fetchTraderOrders } from '../../lib/directus';
import styles from './TraderOrders.module.css';

const ESCROW_STATUS_LABEL = {
  pending_approval:  'Awaiting approval',
  awaiting_finance:  'Awaiting finance',
  finance_approved:  'Paid',
  escrow_locked:     'Paid',
  transport_sourced: 'Paid',
  in_transit:        'In transit',
  delivered:         'Delivered',
  released:          'Delivered',
  rejected:          'Rejected',
};

function mapOrder(o) {
  const batch     = o.batch || {};
  const qty       = parseFloat(batch.quantity) || 0;
  const unitPrice = parseFloat(batch.unit_price) || 0;
  const stockVal  = qty * unitPrice;

  const contact   = o.buyer?.contact;
  const reqBy     = contact
    ? `${contact.first_name || ''} ${contact.last_name || ''}`.trim() || o.buyer?.company_name || '-'
    : o.buyer?.company_name || '-';

  return {
    id:               o.id,
    orderId:          o.order_id ? `#${o.order_id}` : `#ORD-${String(o.id).padStart(6, '0')}`,
    commodity:        batch.commodity || '-',
    grade:            batch.grade || '-',
    quantity:         qty ? `${qty}${batch.unit_of_measure ? ' ' + batch.unit_of_measure : ' MT'}` : '-',
    seller:           batch.seller?.company_name || '-',
    buyer:            o.buyer?.company_name || '-',
    buyerUserId:      o.buyer?.contact?.id || o.buyer?.contact || null,
    requestedBy:      reqBy,
    amount:           stockVal ? `R${stockVal.toLocaleString()}` : '-',
    stockValue:       stockVal ? `R${stockVal.toLocaleString()}` : 'R0',
    deliveryLocation: o.delivery_location || '-',
    escrow_status:    o.escrow_status || 'pending_approval',
    fulfillment_status: o.fulfillment_status || '',
    status:           ESCROW_STATUS_LABEL[o.escrow_status] || o.escrow_status || 'Pending',
    proof_of_payment: o.proof_of_payment || null,
    date_created:     o.date_created,
  };
}

function SuccessModal({ onClose }) {
  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={styles.modal} onClick={e => e.stopPropagation()}>
        <button className={styles.modalClose} onClick={onClose}><LuX size={18} /></button>
        <div className={styles.successIcon}><LuCheck size={32} color="white" /></div>
        <p className={styles.successMsg}>Order review completed successfully</p>
      </div>
    </div>
  );
}

function OrderBadge({ escrowStatus }) {
  const cls =
    escrowStatus === 'pending_approval' ? styles.badgePending :
    escrowStatus === 'rejected'         ? styles.badgeRejected :
    escrowStatus === 'delivered' || escrowStatus === 'released' ? styles.badgeReleased :
    styles.badgeActive;
  const label = {
    pending_approval:  'Awaiting approval',
    awaiting_finance:  'Awaiting finance',
    finance_approved:  'Paid',
    escrow_locked:     'Paid',
    transport_sourced: 'Paid',
    in_transit:        'In transit',
    delivered:         'Delivered',
    released:          'Delivered',
    rejected:          'Rejected',
  }[escrowStatus] || escrowStatus;
  return <span className={`${styles.badge} ${cls}`}>{label}</span>;
}

function OrderCard({ order, onClick }) {
  return (
    <div className={styles.tripCard} onClick={() => onClick(order)}>
      <div className={styles.cardHeader}>
        <div className={styles.cardTop}>
          <span className={styles.loadIdText}>{order.orderId}</span>
          <OrderBadge escrowStatus={order.escrow_status} />
        </div>
      </div>
      <div className={styles.cardBody}>
        <div className={styles.infoRow}>
          <span className={styles.infoLabel}>Commodity</span>
          <span className={styles.infoValue}>{order.commodity}</span>
        </div>
        <div className={styles.infoRow}>
          <span className={styles.infoLabel}>Quantity</span>
          <span className={styles.infoValue}>{order.quantity}</span>
        </div>
        <div className={styles.infoRow}>
          <span className={styles.infoLabel}>Seller</span>
          <span className={styles.infoValue}>{order.seller}</span>
        </div>
        <div className={styles.infoRow}>
          <span className={styles.infoLabel}>Requested by</span>
          <span className={styles.infoValue}>{order.requestedBy}</span>
        </div>
        <div className={styles.cardDivider} />
        <div className={styles.infoRow}>
          <span className={styles.infoLabel}>Amount</span>
          <span className={styles.oliveValue}>{order.amount}</span>
        </div>
      </div>
    </div>
  );
}

export default function TraderOrders() {
  const [orders, setOrders]         = useState([]);
  const [loading, setLoading]       = useState(true);
  const [activeOrder, setActive]    = useState(null);
  const [showSuccess, setSuccess]   = useState(false);
  const [showAllAwaiting, setShowAllAwaiting] = useState(false);
  const [showAllActive, setShowAllActive]     = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    fetchTraderOrders()
      .then(data => setOrders(data.map(mapOrder)))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const awaiting  = orders.filter(o => o.escrow_status === 'pending_approval');
  const active    = orders.filter(o => !['pending_approval', 'rejected'].includes(o.escrow_status));

  const activeCount  = active.length;
  const pendingCount = awaiting.length;
  const inTransit    = orders.filter(o => o.escrow_status === 'in_transit').length;

  function handleCardClick(order) {
    setActive(order);
  }

  return (
    <TraderLayout>
      <div className={styles.page}>
        <h1 className={styles.pageTitle}>Orders</h1>

        {/* Stats */}
        <div className={styles.statsRow}>
          <div className={styles.statCard}>
            <p className={styles.statValue}>{String(activeCount).padStart(2, '0')}</p>
            <p className={styles.statLabel}>active orders</p>
          </div>
          <div className={styles.statCard}>
            <p className={styles.statValue}>{String(pendingCount).padStart(2, '0')}</p>
            <p className={styles.statLabel}>pending approval</p>
          </div>
          <div className={styles.statCard}>
            <p className={styles.statValue}>{String(inTransit).padStart(2, '0')}</p>
            <p className={styles.statLabel}>in transit</p>
          </div>
          <div className={styles.statCard}>
            <p className={styles.statValue}>{String(orders.length).padStart(2, '0')}</p>
            <p className={styles.statLabel}>total orders</p>
          </div>
        </div>

        {loading && <p className={styles.loadingText}>Loading...</p>}

        {!loading && (
          <>
            {/* Awaiting approval */}
            {awaiting.length > 0 && (
              <div className={styles.section}>
                <div className={styles.sectionHeader}>
                  <p className={styles.sectionTitle}>Awaiting approval</p>
                  {awaiting.length > 6 && (
                    <button className={styles.seeAll} onClick={() => setShowAllAwaiting(v => !v)}>
                      {showAllAwaiting ? 'Show less' : 'See all'}
                    </button>
                  )}
                </div>
                <div className={styles.ordersGrid}>
                  {(showAllAwaiting ? awaiting : awaiting.slice(0, 6)).map(order => (
                    <OrderCard key={order.id} order={order} onClick={handleCardClick} />
                  ))}
                </div>
              </div>
            )}

            {/* Active escrow */}
            {active.length > 0 && (
              <div className={styles.section}>
                <div className={styles.sectionHeader}>
                  <p className={styles.sectionTitle}>Active escrow</p>
                  {active.length > 6 && (
                    <button className={styles.seeAll} onClick={() => setShowAllActive(v => !v)}>
                      {showAllActive ? 'Show less' : 'See all'}
                    </button>
                  )}
                </div>
                <div className={styles.ordersGrid}>
                  {(showAllActive ? active : active.slice(0, 6)).map(order => (
                    <OrderCard key={order.id} order={order} onClick={handleCardClick} />
                  ))}
                </div>
              </div>
            )}

            {orders.length === 0 && (
              <p className={styles.emptyText}>No orders yet.</p>
            )}
          </>
        )}
      </div>

      <TraderOrderDrawer
        order={activeOrder}
        onClose={() => setActive(null)}
        onUpdated={() => { load(); setSuccess(true); }}
      />

      {showSuccess && <SuccessModal onClose={() => setSuccess(false)} />}
    </TraderLayout>
  );
}
